/*
 * Lunvelis Partners · recursos de interacción (implementaciones propias, sin dependencias).
 * Inspirados en patrones de las bibliotecas consultadas; no se copió código de ninguna:
 *   - Command (shadcn/ui, cmdk) con semántica combobox/listbox (Radix, React Aria): paleta de navegación y servicios.
 *   - Confetti y Ripple (Magic UI): celebración al confirmar un envío y respuesta al pulsar.
 *   - Dock (Magic UI / Aceternity Floating Dock): canales con ampliación según la distancia del puntero.
 *   - Marquee (Magic UI / Aceternity Infinite Moving Cards): categorías reales de Lunvelis World.
 *   - Typing Animation (Magic UI): el «camino más directo» de Contacto se escribe al cambiar de tema.
 *   - Animated Toast (Sonner/shadcn) con resorte (Motion): avisos de pasos, filtros y precios.
 * Todo es mejora progresiva y respeta prefers-reduced-motion.
 */
(() => {
  if (window.__lpFx) return;
  window.__lpFx = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const ui = () => window.LunvelisUI;
  const toast = (options) => (ui() ? ui().toast(options) : null);
  const plain = (value) => String(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  /* ---------- Confetti ---------- */
  const COLORS = ['#ee6f57', '#2379eb', '#2f9e6b', '#e8a63a', '#111418', '#ffffff'];
  const confetti = (origin, { count = 70, spread = 1 } = {}) => {
    if (reduced.matches || document.hidden) return;
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;inline-size:100%;block-size:100%;pointer-events:none;z-index:150';
    const ratio = Math.min(devicePixelRatio || 1, 2);
    canvas.width = innerWidth * ratio;
    canvas.height = innerHeight * ratio;
    document.body.append(canvas);
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    const box = origin && origin.getBoundingClientRect ? origin.getBoundingClientRect() : { left: innerWidth / 2, top: innerHeight / 2, width: 0, height: 0 };
    const x0 = Math.min(innerWidth - 20, Math.max(20, box.left + box.width / 2));
    const y0 = Math.min(innerHeight - 20, Math.max(20, box.top + Math.min(box.height / 2, 120)));
    const parts = Array.from({ length: count }, () => {
      const angle = (-Math.PI / 2) + (Math.random() - 0.5) * Math.PI * 1.1 * spread;
      const speed = 5 + Math.random() * 8;
      return { x: x0, y: y0, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed, w: 5 + Math.random() * 6, h: 3 + Math.random() * 5, r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.4, c: COLORS[Math.floor(Math.random() * COLORS.length)] };
    });
    const start = performance.now();
    const frame = (now) => {
      const t = now - start;
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      for (const p of parts) {
        p.vy += 0.28; p.vx *= 0.992; p.x += p.vx; p.y += p.vy; p.r += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - t / 1600);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (t < 1600) requestAnimationFrame(frame); else canvas.remove();
    };
    requestAnimationFrame(frame);
  };

  /* ---------- Ripple ---------- */
  const RIPPLE = '.lp-btn, .lp-pcard__add, .lp-start-card, .lp-seg button, .lp-finder__tab, .lp-dock__item, .lp-reason, .lp-cmd__item';
  document.addEventListener('pointerdown', (event) => {
    if (reduced.matches || event.button > 0) return;
    const host = event.target.closest && event.target.closest(RIPPLE);
    if (!host || host.disabled) return;
    let layer = host.querySelector(':scope > .lp-ripple-layer');
    if (!layer) { layer = document.createElement('span'); layer.className = 'lp-ripple-layer'; layer.setAttribute('aria-hidden', 'true'); host.append(layer); }
    const box = host.getBoundingClientRect();
    const size = Math.max(box.width, box.height) * 2;
    const dot = document.createElement('span');
    dot.className = 'lp-ripple';
    dot.style.cssText = `inline-size:${size}px;block-size:${size}px;inset-inline-start:${event.clientX - box.left - size / 2}px;inset-block-start:${event.clientY - box.top - size / 2}px`;
    layer.append(dot);
    dot.addEventListener('animationend', () => dot.remove(), { once: true });
  }, { passive: true });

  /* ---------- Dock ---------- */
  const setupDock = () => {
    for (const dock of document.querySelectorAll('.lp-dock:not([data-fx])')) {
      dock.dataset.fx = '';
      const items = [...dock.querySelectorAll('.lp-dock__item')];
      if (reduced.matches || !matchMedia('(hover: hover)').matches) continue;
      dock.addEventListener('pointermove', (event) => {
        for (const item of items) {
          const box = item.getBoundingClientRect();
          const distance = Math.abs(event.clientX - (box.left + box.width / 2));
          const scale = 1 + 0.55 * Math.max(0, 1 - distance / 130);
          item.style.setProperty('--s', scale.toFixed(3));
        }
      });
      dock.addEventListener('pointerleave', () => items.forEach((item) => item.style.removeProperty('--s')));
    }
  };

  /* ---------- Marquee y cifras vivas de Lunvelis World ---------- */
  const setupWorldBlocks = () => {
    const marquee = document.querySelector('[data-world-marquee]');
    const stats = document.querySelector('[data-world-areastats]');
    if (!marquee && !stats) return;
    const start = () => {
      const W = window.LunvelisWorld;
      if (!W) { setTimeout(start, 150); return; }
      W.load().then((list) => {
        if (stats) {
          const byArea = new Map();
          list.forEach((product) => { const entry = byArea.get(product.vendor) || { count: 0, min: Infinity, max: 0, cats: new Set() }; entry.count += 1; if (product.min !== null) { entry.min = Math.min(entry.min, product.min); entry.max = Math.max(entry.max, product.min); } entry.cats.add(W.categoryOf(product)); byArea.set(product.vendor, entry); });
          stats.replaceChildren(...[...byArea.entries()].sort((a, b) => b[1].count - a[1].count).map(([vendor, entry], index) => {
            const card = document.createElement('article');
            card.className = `lp-areastat lp-beam lp-tone-${index}`;
            const label = document.createElement('p'); label.className = 'lp-areastat__area'; label.textContent = W.areaOf({ vendor });
            const big = document.createElement('p'); big.className = 'lp-areastat__count'; big.innerHTML = `<b data-count="${entry.count}">${entry.count}</b> servicios publicados`;
            const sub = document.createElement('p'); sub.className = 'lp-areastat__range'; sub.textContent = `${entry.cats.size} categorías · desde ${W.money.format(entry.min)} hasta ${W.money.format(entry.max)}`;
            card.append(label, big, sub);
            return card;
          }));
          stats.hidden = false;
        }
        if (marquee) {
          const track = marquee.querySelector('.lp-marquee__track');
          const counts = new Map();
          list.forEach((product) => { const key = `${W.areaOf(product)}|${W.categoryOf(product)}|${product.vendor}`; counts.set(key, (counts.get(key) || 0) + 1); });
          const chips = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([key, count], index) => {
            const [area, category, vendor] = key.split('|');
            const link = document.createElement('a');
            link.className = `lp-marquee__chip lp-tone-${area === 'Storm' ? 1 : 0}`;
            link.href = `/pages/partners-services?area=${encodeURIComponent(area)}&cat=${encodeURIComponent(category)}#catalogo`;
            link.dataset.catalogJump = vendor; link.dataset.catalogCat = category;
            link.textContent = `${area} · ${category} · ${count}`;
            if (index > 0 && false) link.tabIndex = -1;
            return link;
          });
          const group = (hidden) => { const div = document.createElement('div'); div.className = 'lp-marquee__group'; if (hidden) div.setAttribute('aria-hidden', 'true'); (hidden ? chips.map((chip) => { const clone = chip.cloneNode(true); clone.tabIndex = -1; return clone; }) : chips).forEach((chip) => div.append(chip)); return div; };
          track.replaceChildren(group(false), group(true));
          marquee.hidden = false;
        }
      }).catch(() => { /* sin conexión: el bloque queda oculto */ });
    };
    start();
  };

  /* ---------- Typing ---------- */
  const type = (element) => {
    const full = element.textContent;
    if (reduced.matches || !full) return;
    element.textContent = '';
    element.setAttribute('aria-label', full);
    let index = 0;
    const step = () => { index = Math.min(full.length, index + 2); element.textContent = full.slice(0, index); if (index < full.length) setTimeout(step, 18); else element.removeAttribute('aria-label'); };
    step();
  };
  document.addEventListener('change', (event) => {
    if (!event.target.matches || !event.target.matches('.lp-reason input')) return;
    setTimeout(() => { const text = document.querySelector('[data-reason-panel] p:not(.lp-route__label)'); if (text) type(text); }, 30);
  });

  /* ---------- Avisos de pasos, precios y cuenta ---------- */
  document.addEventListener('click', (event) => {
    const t = event.target;
    if (!t.closest) return;
    const next = t.closest('[ref="next"], [data-assessment-next]');
    if (next) {
      const label = () => (document.querySelector('.partners-intake__progress span, [data-assessment-progress]') || {}).textContent || '';
      const before = label();
      setTimeout(() => { const after = label(); if (after && after !== before) toast({ type: 'success', id: 'lp-step', title: 'Paso completado', message: after.trim(), duration: 2200 }); }, 260);
    }
    const billing = t.closest('[data-billing-value]');
    if (billing) toast({ type: 'info', id: 'lp-billing', title: billing.dataset.billingValue === 'year' ? 'Mostrando precios anuales' : 'Mostrando precios mensuales', message: 'Precios oficiales en pesos mexicanos (MXN).', duration: 2600 });
    const login = t.closest('#lp-account a.lp-btn');
    if (login) toast({ type: 'progress', id: 'lp-login', title: 'Abriendo el acceso seguro de Shopify…', message: 'Te enviará un código a tu correo para verificar tu identidad.', duration: 6000 });
    const copy = t.closest('[data-lp-copy]');
    if (copy) { (navigator.clipboard ? navigator.clipboard.writeText(copy.dataset.lpCopy) : Promise.reject()).then(() => toast({ type: 'success', title: 'Copiado', message: copy.dataset.lpCopy, duration: 2400 }), () => toast({ type: 'warning', title: 'No se pudo copiar', message: copy.dataset.lpCopy })); }
  });

  /* ---------- Paleta de comandos (⌘K / Ctrl+K / «/» / icono de búsqueda) ---------- */
  const PAGES = [
    ['Inicio', 'Presentación general de Partners', '/'],
    ['Soluciones', 'Qué necesitas lograr y qué servicios hay', '/pages/partners-services'],
    ['Cómo funciona', 'El proceso paso a paso', '/pages/partners-journey'],
    ['Colaborar', 'Modalidades y propuesta', '/pages/partners-opportunities'],
    ['Contacto', 'Habla con el equipo', '/pages/partners-contact'],
    ['Membresías de Partners', 'Niveles y precios oficiales', '/pages/partners-plans#partners-plans'],
    ['Membresías de Lunvelis World', 'Studio, publicadas en la tienda', '/pages/partners-plans#world'],
    ['Catálogo de Lunvelis World', 'Servicios, categorías y precios en vivo', '/pages/partners-services#catalogo'],
    ['Empezar el diagnóstico', 'Seis pasos para ordenar lo que necesitas', '/pages/partners-assessment'],
    ['Agendar una reunión', 'Solicita fecha, hora y modalidad', '/pages/partners-meetings'],
    ['Mi espacio Partners', 'Tareas, oportunidades y recursos', '/pages/partners-workspace'],
    ['Soporte', 'Ayuda con una membresía o una sesión', '/pages/partners-support'],
    ['Canales y redes', 'WhatsApp, correo, Instagram y TikTok', '/pages/partners-connect']
  ];
  const ACTIONS = [
    ['Abrir mi cuenta', 'Iniciar sesión o crear cuenta', () => document.querySelector('.account-button')?.click()],
    ['Ver mi selección de servicios', 'Servicios elegidos de Lunvelis World', () => document.getElementById('lp-selection-pill')?.click()],
    ['Escribir por WhatsApp', 'Conversación directa con el equipo', 'https://wa.me/5215619973776'],
    ['Ir a Lunvelis World', 'La tienda con todos los servicios', 'https://lunvelis.world']
  ];
  const setupCommand = () => {
    const dialog = document.getElementById('lp-command');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const input = dialog.querySelector('input');
    const list = dialog.querySelector('[role="listbox"]');
    const status = dialog.querySelector('[data-cmd-status]');
    let items = [];
    let active = 0;
    let services = [];
    let opener = null;
    const render = () => {
      const q = plain(input.value.trim());
      const words = q.split(/\s+/).filter(Boolean);
      const hit = (text) => words.every((word) => plain(text).includes(word));
      const groups = [];
      const pages = PAGES.filter(([title, hint]) => !q || hit(`${title} ${hint}`)).map(([title, hint, href]) => ({ title, hint, run: () => { location.href = href; } , href }));
      const actions = ACTIONS.filter(([title, hint]) => !q || hit(`${title} ${hint}`)).map(([title, hint, target]) => ({ title, hint, run: () => (typeof target === 'function' ? target() : window.open(target, '_blank', 'noopener')) }));
      const found = q.length >= 2 ? services.filter((product) => hit(`${product.title} ${product.type}`)).slice(0, 6).map((product) => ({ title: product.title, hint: `${window.LunvelisWorld.areaOf(product)} · ${window.LunvelisWorld.categoryOf(product)} · ${window.LunvelisWorld.price(product)}`, run: () => { location.href = `/pages/partners-services?q=${encodeURIComponent(product.title)}#catalogo`; } })) : [];
      if (pages.length) groups.push(['Ir a', pages]);
      if (actions.length) groups.push(['Acciones', actions]);
      if (found.length) groups.push(['Servicios de Lunvelis World', found]);
      items = groups.flatMap(([, entries]) => entries);
      active = Math.min(active, Math.max(0, items.length - 1));
      list.replaceChildren();
      let index = 0;
      for (const [label, entries] of groups) {
        const group = document.createElement('li'); group.setAttribute('role', 'presentation'); group.className = 'lp-cmd__group'; group.textContent = label; list.append(group);
        for (const entry of entries) {
          const option = document.createElement('li');
          option.className = 'lp-cmd__item'; option.setAttribute('role', 'option'); option.id = `lp-cmd-${index}`;
          option.setAttribute('aria-selected', String(index === active));
          const strong = document.createElement('strong'); strong.textContent = entry.title;
          const small = document.createElement('small'); small.textContent = entry.hint;
          option.append(strong, small);
          const mine = index;
          option.addEventListener('click', () => choose(mine));
          option.addEventListener('pointermove', () => { if (active !== mine) { active = mine; paintActive(); } });
          list.append(option);
          index += 1;
        }
      }
      if (!items.length) { const empty = document.createElement('li'); empty.className = 'lp-cmd__empty'; empty.textContent = 'Sin resultados. Prueba con otra palabra, como «diagnóstico», «precio» o «video».'; list.append(empty); }
      input.setAttribute('aria-activedescendant', items.length ? `lp-cmd-${active}` : '');
      status.textContent = items.length ? `${items.length} resultados` : 'Sin resultados';
    };
    const paintActive = () => { list.querySelectorAll('[role="option"]').forEach((option, i) => { option.setAttribute('aria-selected', String(i === active)); if (i === active) option.scrollIntoView({ block: 'nearest' }); }); input.setAttribute('aria-activedescendant', `lp-cmd-${active}`); };
    const choose = (index) => { const entry = items[index]; if (!entry) return; dialog.close(); entry.run(); };
    const open = (from) => {
      if (dialog.open) return;
      opener = from || document.activeElement;
      input.value = ''; active = 0; render(); dialog.showModal(); input.focus();
      if (window.LunvelisWorld && !services.length) window.LunvelisWorld.load().then((all) => { services = all; if (dialog.open) render(); }).catch(() => {});
    };
    input.addEventListener('input', () => { active = 0; render(); });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') { event.preventDefault(); active = (active + 1) % Math.max(1, items.length); paintActive(); }
      else if (event.key === 'ArrowUp') { event.preventDefault(); active = (active - 1 + items.length) % Math.max(1, items.length); paintActive(); }
      else if (event.key === 'Enter') { event.preventDefault(); choose(active); }
    });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => { if (opener && opener.focus) opener.focus(); });
    document.addEventListener('keydown', (event) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test((event.target || {}).tagName) || (event.target && event.target.isContentEditable);
      if (((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') || (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey)) { event.preventDefault(); open(); }
    });
    window.addEventListener('click', (event) => {
      const path = event.composedPath ? event.composedPath() : [event.target];
      const trigger = path.find((node) => node instanceof Element && node.matches('.search-action, .search-action *, [data-lp-command-open]'));
      if (!trigger) return;
      event.preventDefault(); event.stopImmediatePropagation(); open(trigger.closest('button, a') || trigger);
    }, true);
  };

  /* El icono de búsqueda del encabezado abría el buscador de productos de Shopify (esta tienda no tiene productos):
     se le quita el manejador declarativo para que solo abra la paleta de comandos. */
  const retargetSearch = () => {
    document.querySelectorAll('.search-action button').forEach((button) => {
      for (const name of [...button.getAttributeNames()]) if (name.startsWith('on:')) button.removeAttribute(name);
      button.setAttribute('data-lp-command-open', '');
      button.setAttribute('aria-haspopup', 'dialog');
    });
  };

  window.LunvelisFx = { confetti, type };
  const boot = () => { retargetSearch(); setupDock(); setupWorldBlocks(); setupCommand(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
